import { ArgumentUndefinedError } from '#errors/argument-undefined.error';
import { ensureError } from '#errors/ensure';
import { isNotDefined } from '#misc/is-not-defined.guard';
import { Result } from '#result/result';

/**
 * Safely parses a JSON string into a typed object.
 *
 * @template TData The expected type of the parsed object.
 * @param value The JSON string to parse, or null.
 * @returns A {@link Result} containing the parsed object on success,
 * or an {@link ArgumentUndefinedError} on failure.
 *
 * If `value` is `null` or `undefined`, returns a failure result with an `ArgumentUndefinedError`.
 * If parsing fails due to invalid JSON, returns a failure result with the parsing error.
 */
export const parseJSON = <TData = unknown>(value: null | string): Result<TData, ArgumentUndefinedError> => {
  if (isNotDefined(value)) {
    return Result.failure(new ArgumentUndefinedError('value'));
  }

  try {
    const parsed = JSON.parse(value) as TData;

    return Result.success(parsed);
  } catch (e) {
    return Result.failure(ensureError(e));
  }
};
